import { useEffect } from 'react';
import {
  atom,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import produce from 'immer';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { findLastIndex, isNumber } from 'lodash';

import * as K from 'common/constants';
import { QueryOrigin, QueryType } from 'common/graphqlTypes';
import log from 'common/ScalyrLogger';
import { getRequestIds } from 'common/util/lrqRequest';

import { activeSearchMatchCountEstimateState } from 'api/apolloClient.state';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import {
  generateContinuationRequest,
  generateNewSearchLogsRequests,
  LRQRequest,
} from 'hooks/useLRQ/useLRQ';
import {
  completeFilterState,
  locationState,
  queryTimeRangeState,
  selectedTokenState,
  teamEmailsState,
} from 'components/common/Managers/RouteStateManager.state';
import { CustomLocationState } from 'components/common/Managers/RouteStateManager.utils';
import {
  logResultsScrollOffset,
  visibleCenterIndexState,
} from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import { wrappedLinesScrollbarWidthState } from 'components/common/Managers/LayoutStateManager';
import { findAnchorCursor, initializePaginatedRequests } from 'components/search/searchUtils';
import { useGetFullLogData } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';

import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import {
  anchorLogRequestsState,
  eventSortOrderState,
  initialLogRequestsState,
  isScrollReadyState,
  isSearchPageResultsPendingSelector,
  paginatedLogRequestsState,
  pinLogDetailsState,
  searchPageStatusSelector,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';

/**
 * Array of boolean values representing whether the user has scrolled down far enough to fetch more results.
 */
export const downwardThresholdState = atom<Array<boolean | undefined>>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_DOWN_THRESHOLD_STATE,
  default: [],
});

/**
 * Array of boolean values representing whether the user has scrolled up far enough to fetch more results.
 */
export const upwardThresholdState = atom<Array<boolean | undefined>>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_UP_THRESHOLD_STATE,
  default: [],
});

/**
 * Tracks the (continuationToken) cursor anchoring the results for the current pagination context, which might be the
 * first token in an ascending context, the last token in a descending context, or the continuationToken offset for a
 * centered search.
 */
export const anchorCursorState = atom<string>({
  key: K.RECOIL_ATOM_KEY.ANCHOR_CURSOR,
  default: '',
});

/**
 * Provides alert indication that the search page is pending results.
 * On long running queries, this alert is gracefully replaced by LRQ indication alert after the first pingQuery request is issued
 */
function useSearchIndicationAlert(): void {
  const isSearchPageResultsPending = useRecoilValue(isSearchPageResultsPendingSelector);

  /**
   * Subroutine to set {@see pageAlertConfigState} to `NO_PAGE_ALERT`, but only if the current id matches
   * `PAGE_ALERT_ID.SEARCHING`. Otherwise, this subroutine is a no-op so that this feature does not interfere with other
   * alerts.
   */
  const disableAlert = useRecoilCallback(({ set, snapshot }) => (): void => {
    if (snapshot.getLoadable(pageAlertConfigState).getValue().id !== K.PAGE_ALERT_ID.SEARCHING) { return; }

    set(pageAlertConfigState, K.NO_PAGE_ALERT);
  });

  useEffect(useRecoilCallback(({ set, snapshot }) => (): () => void => {
    const alertMessage = 'Searching…';
    const enableAlert = (): void => {
      set(pageAlertConfigState, {
        id: K.PAGE_ALERT_ID.SEARCHING,
        message: alertMessage,
        variant: PageAlertVariant.INFO,
        dismissible: false,
      });
    };

    if (!isSearchPageResultsPending) {
      disableAlert();
    }

    if (isSearchPageResultsPending && snapshot.getLoadable(pageAlertConfigState).getValue() === K.NO_PAGE_ALERT) {
      enableAlert();
    }

    return disableAlert;
  }, [isSearchPageResultsPending]), [isSearchPageResultsPending]);

}

/**
 * Provides cleanup logic for the `K.COMPONENT_STATUS` values on the search page on unmount (i.e. on route change)
 */
function useSearchPageStatusCleanup(): void {
  const setAllSearchPageStatuses = useSetRecoilState(searchPageStatusSelector);

  useEffect(() => {
    const cleanup = (): void => {
      // @ts-expect-error
      setAllSearchPageStatuses(K.COMPONENT_STATUS.IDLE);
    };

    return cleanup;
  }, [setAllSearchPageStatuses]);
}

/**
 * This component manages top-level state for the Search Logs Page. Note that:
 *
 *    This is a child of the Search Logs Page component
 *    No content is ever actually rendered by this component tree
 *    This is a sister to all of the component trees that do render content on the Search Logs Page
 *
 * This structure allows frequent cheap renders in this tree without leaking unnecessary renders throughout the page.
 */

function SearchStateManager(): JSX.Element {

  const [ initialLogRequests, setInitialLogRequests ] = useRecoilState(initialLogRequestsState);
  const [ anchorLogRequests, setAnchorLogRequests ] = useRecoilState(anchorLogRequestsState);
  const [ paginatedLogRequests, setPaginatedLogRequests ] = useRecoilState(paginatedLogRequestsState);
  const setScrollOffset = useSetRecoilState(logResultsScrollOffset);
  const resetScrollOffset = useResetRecoilState(logResultsScrollOffset);
  const setActiveSearchMatchCount = useSetRecoilState(activeSearchMatchCountEstimateState);
  const resetActiveSearchMatchCount = useResetRecoilState(activeSearchMatchCountEstimateState);
  const [ anchorCursor, setAnchorCursor ] = useRecoilState(anchorCursorState);
  const resetAnchorCursor = useResetRecoilState(anchorCursorState);
  const resetIsScrollReady = useResetRecoilState(isScrollReadyState);
  const resetVisibleCenterIndex = useResetRecoilState(visibleCenterIndexState);

  const anchorIds = getRequestIds(anchorLogRequests, QueryType.LOG);
  const { results: anchorResults } = useEventCache(anchorIds);
  const isAnchorDone = anchorResults.every((result) => result.isDone);

  // The matchCount returned by LOG queries is unreliable, so we treat the matchCount returned from the initial PLOT
  // query for the timeline bar graph as the canonical matchCount for this result set
  const plotRequestIds = getRequestIds(initialLogRequests, QueryType.PLOT);
  const initialTimelineLineGraphId = plotRequestIds?.[1] || '';
  const { results: [{ plotData: { matchCount } }] } = useEventCache([initialTimelineLineGraphId]);

  const logEntryIds = getRequestIds(paginatedLogRequests, QueryType.LOG);
  const { paginatedData: { currentIds, cursors, previousIds } } = useEventCache(logEntryIds, true);

  const resetVerticalScrollbarWidth = useResetRecoilState(wrappedLinesScrollbarWidthState);
  const [ downwardThresholds, setDownwardThresholds ] = useRecoilState(downwardThresholdState);
  const resetDownwardThresholds = useResetRecoilState(downwardThresholdState);
  const [ upwardThresholds, setUpwardThresholds ] = useRecoilState(upwardThresholdState);
  const resetUpwardThresholds = useResetRecoilState(upwardThresholdState);
  const teamEmails = useRecoilValue(teamEmailsState);
  const [selectedToken, setSelectedToken ] = useRecoilState(selectedTokenState);
  const isPinnedView = useRecoilValue(pinLogDetailsState);
  const sortOrder = useRecoilValue(eventSortOrderState);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const isNewSearch = useIsNewSearch();
  const completeFilter = useRecoilValue(completeFilterState);
  // grab disposable param values from location.state
  const historyLocation = useRecoilValue(locationState);
  const historyLocationState = historyLocation.state as CustomLocationState;
  const centered = historyLocationState?.disposableParams?.[K.URL_PARAM.CENTERED] || '';
  const continuationToken = historyLocationState?.disposableParams?.[K.URL_PARAM.CONTINUATION_TOKEN] || '';
  const { startTime, endTime, startTimeNs, endTimeNs, requestTime } = queryTimeRange;

  // generate and handle 'getLogEntry' requests for full data for a log line
  useGetFullLogData();

  // Clean up search page state, when the page is unmounted
  useEffect(() => (): void => {
    resetVerticalScrollbarWidth();
    resetDownwardThresholds();
    resetUpwardThresholds();
    resetScrollOffset();
    resetActiveSearchMatchCount();
    resetAnchorCursor();
    resetIsScrollReady();
    resetVisibleCenterIndex();
    setInitialLogRequests(K.EMPTY_ARRAY);
    setAnchorLogRequests(K.EMPTY_ARRAY);
    setPaginatedLogRequests(K.EMPTY_ARRAY);
  }, [
    resetVerticalScrollbarWidth,
    resetDownwardThresholds,
    resetUpwardThresholds,
    resetScrollOffset,
    resetActiveSearchMatchCount,
    resetAnchorCursor,
    resetIsScrollReady,
    resetVisibleCenterIndex,
    setInitialLogRequests,
    setAnchorLogRequests,
    setPaginatedLogRequests,
  ]);

  // Alert indication that the search page is pending results
  useSearchIndicationAlert();

  // cleanup component statuses on the page unmount (i.e. on route change)
  useSearchPageStatusCleanup();

  // Initial Request
  useConditionalEffect(() => {

    log.debug('New search for "' + completeFilter + '" , from ' + (new Date(Number(startTime))) + ' to ' + (new Date(Number(endTime))));

    const requests = generateNewSearchLogsRequests({
      centered:     centered === 'true',
      endTime:      endTimeNs,
      filter:       completeFilter,
      offset:       continuationToken,
      origin:       QueryOrigin.SEARCH,
      requestTime,
      showProgress: true,
      sortOrder,
      startTime:    startTimeNs,
      teamEmails,
    });

    setInitialLogRequests(requests);
    setAnchorLogRequests(requests);
    setPaginatedLogRequests(K.EMPTY_ARRAY);
    resetVerticalScrollbarWidth();
    resetIsScrollReady();
    resetVisibleCenterIndex();
  }, isNewSearch);

  // When the anchor results are returned from the server we set up the pagination model and initialize associated state
  useConditionalEffect(() => {
    const newAnchorCursor = findAnchorCursor(anchorResults);
    setAnchorCursor(newAnchorCursor);
    setScrollOffset({ align: 'center', cursor: newAnchorCursor });
    // When pinned inspect view is open, select and show anchor element log line by default
    if (selectedToken && isPinnedView) {
      setSelectedToken(newAnchorCursor);
    }
    setActiveSearchMatchCount(matchCount);
    // TODO: can we verify that there are no bugs from passing an estimated matchCount to initializePaginatedRequests?
    // {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5520}
    const initialPaginatedLogRequests = initializePaginatedRequests(anchorLogRequests, anchorResults, matchCount, sortOrder);
    const initialThresholds = new Array(initialPaginatedLogRequests.length).fill(false);
    setDownwardThresholds(initialThresholds);
    setUpwardThresholds(initialThresholds);
    setPaginatedLogRequests(initialPaginatedLogRequests);
  }, isAnchorDone && paginatedLogRequests.length === 0 && !!startTime && matchCount !== undefined);

  /**
   * Two conditions are required to add a new element to the logEntryRequests list, which triggers a new paginated
   * request for log entries:
   *
   * 1. The user has scrolled past the necessary threshold.
   * 2. The last edge of the adjacent request has been loaded. The continuationToken of this (cursor) edge is the offset
   *    for the new request.
   */
  const downwardIndex = findLastIndex(paginatedLogRequests, (request, index) => !request && !!cursors[index - 1] && !!downwardThresholds[index - 1]);
  const downwardCursor = cursors[downwardIndex - 1];
  const isNewDownwardRequest = downwardIndex !== -1;

  const upwardIndex = paginatedLogRequests.findIndex((request, index) => !request && !!cursors[index + 1] && !!upwardThresholds[index + 1]);
  const upwardCursor = cursors[upwardIndex + 1];
  const isNewUpwardRequest = upwardIndex !== -1;
  const isTerminalRequest = upwardIndex === 0 || downwardIndex === paginatedLogRequests.length - 1;

  useConditionalEffect(() => {

    // If this is a terminal request, and there are additional results in the result set that are not yet included in
    // the log id list (and they still won't be included after this terminal request), calculate the number of
    // additional requests up to 10 required to satisfy the remaining results.
    let additionalRequests: Array<undefined> | undefined;
    const projectedLengthAfterNewRequest = previousIds.length + K.REQUEST_BLOCK_SIZE;
    if (matchCount > projectedLengthAfterNewRequest && isTerminalRequest) {
      additionalRequests = new Array<undefined>(Math.min(10, Math.ceil((matchCount - projectedLengthAfterNewRequest) / K.REQUEST_BLOCK_SIZE)));
    }

    let downwardRequest: LRQRequest;
    let upwardRequest: LRQRequest;
    if (isNewDownwardRequest) {
      log.debug('Continued search for "' + completeFilter + '" , from ' + (new Date(Number(startTime))) + ' to ' + (new Date(Number(endTime))));

      downwardRequest = generateContinuationRequest({
        endTime:     endTimeNs,
        filter:      completeFilter,
        origin:      QueryOrigin.SEARCH,
        requestTime,
        startTime:   startTimeNs,
        teamEmails,
      }, downwardCursor, K.SCROLL_DIRECTION.DOWN, sortOrder);
    }

    if (isNewUpwardRequest) {
      log.debug('Continued search for "' + completeFilter + '" , from ' + (new Date(Number(startTime))) + ' to ' + (new Date(Number(endTime))));

      upwardRequest = generateContinuationRequest({
        endTime:     endTimeNs,
        filter:      completeFilter,
        origin:      QueryOrigin.SEARCH,
        requestTime,
        startTime:   startTimeNs,
        teamEmails,
      }, upwardCursor, K.SCROLL_DIRECTION.UP, sortOrder);
    }

    setPaginatedLogRequests(produce(paginatedLogRequests, (draft: Array<LRQRequest | undefined>) => {
      if (isNewDownwardRequest && !draft[downwardIndex]) {
        draft[downwardIndex] = downwardRequest;
        if (additionalRequests) {
          draft.push(...additionalRequests);
          setDownwardThresholds((thresholds) => thresholds.concat(additionalRequests));
          setUpwardThresholds((thresholds) => thresholds.concat(additionalRequests));
        }
      }
      if (isNewUpwardRequest && !draft[upwardIndex]) {
        draft[upwardIndex] = upwardRequest;
        if (additionalRequests) {
          draft.unshift(...additionalRequests);
          setDownwardThresholds((thresholds) => (additionalRequests as Array<boolean | undefined>).concat(thresholds));
          setUpwardThresholds((thresholds) => (additionalRequests as Array<boolean | undefined>).concat(thresholds));
          resetIsScrollReady();
        }
      }
    }));
  }, isNewDownwardRequest || isNewUpwardRequest);

  /**
   * When the list is mutated in a way that changes the index of the anchor cursor, we need to reset the user's scroll
   * position by the same amount. For example, If 2,000 new elements are prepended to the list, the log lines the user
   * is currently viewing will suddenly be at indexes 2,000 more than they were a moment ago. On the other hand, if
   * 2,000 new elements are appended to the end of the list, the anchor will be unchanged, and the user's view context
   * does not need to be adjusted.
   */
  const prevAnchorCursor = usePrevious(anchorCursor);
  const anchorIndex = anchorCursor ? currentIds.indexOf(anchorCursor) : -1;
  const prevAnchorIndexOrUndef = usePrevious(anchorIndex);
  const prevAnchorIndex = prevAnchorIndexOrUndef !== undefined ? prevAnchorIndexOrUndef : -1;
  const anchorIndexDiff = anchorIndex - prevAnchorIndex;
  const fixVisibleIndexCondition = anchorCursor === prevAnchorCursor && anchorIndex !== prevAnchorIndex && anchorIndex > -1 && prevAnchorIndex > -1;
  const fixVisibleIndexCallback = useRecoilCallback(({ snapshot, set }) => (): void => {
    const visibleCenterIndex = snapshot.getLoadable(visibleCenterIndexState).getValue().index;
    if (isNumber(visibleCenterIndex)) {
      set(isScrollReadyState, false);
      set(logResultsScrollOffset, { align: 'center', index: visibleCenterIndex + anchorIndexDiff });
    }
  }, [ anchorIndexDiff ]);
  useConditionalEffect(fixVisibleIndexCallback, fixVisibleIndexCondition);

  // Render requests

  const requestsByKey = paginatedLogRequests
    .filter((request) => !!request)
    .concat(anchorLogRequests)
    .concat(initialLogRequests)
    .reduce((collection, request) => ({ ...collection, [request?.key || '']: request }), {}) as {[key: string]: LRQRequest};
  return (
    <>
      {
        Object.entries(requestsByKey).map(
          ([ key, request ]: [string, LRQRequest]) => (
            <LRQRequestManager
              showProgress={request?.showProgress || false}
              key={key}
              request={request as LRQRequest}
            />
          ),
        )
      }
    </>
  );
}

export default SearchStateManager;
